import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useContextualNavigation } from '../hooks/useContextualNavigation';
import { ArrowLeft, Loader2, AlertTriangle, Trash2, RotateCcw, Clock, Play, Pause, CheckCircle, AlertCircle, ExternalLink, Edit2, History, Columns } from 'lucide-react';
import { apiService } from '../services/api';
import { Queue, CircuitBreakerState, CircuitBreakerSettings as CircuitBreakerSettingsType, SchedulingSettings, QueueStats, QueueItem, Model } from '../types';
import CircuitBreakerSettings from '../components/Queue/CircuitBreakerSettings';
import ResetCircuitBreakerButton from '../components/Queue/ResetCircuitBreakerButton';
import QueueSchedulingSettings from '../components/Queue/QueueSchedulingSettings';
import QueueItemsList from '../components/Queue/QueueItemsList';
import QueueItemDetailsModal from '../components/Queue/QueueItemDetailsModal';
import ConsumerRegistryPanel from '../components/Queue/ConsumerRegistryPanel';
import QueueEditModal from '../components/Queue/QueueEditModal';
import { PriorityHistogram } from '../components/Queue/PriorityHistogram';
import EntityHistoryTab from '../components/history/EntityHistoryTab';
import Tooltip from '../components/common/Tooltip';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import toast from 'react-hot-toast';

/**
 * QueueDetailsPage - Display and configure a single queue
 *
 * Route: /projects/:projectId/queues/:queueId
 *
 * Features:
 * - Display queue details (name, description, timestamps)
 * - Show circuit breaker status
 * - CircuitBreakerSettings form for configuration
 * - ResetCircuitBreakerButton when circuit is OPEN
 * - Back link to queue list
 * - Loading and error states
 */
const QueueDetailsPage: React.FC = () => {
  const { projectId, queueId } = useParams<{ projectId: string; queueId: string }>();

  // Contextual back navigation - uses router state if available, falls back to default
  const { getBackNavigation } = useContextualNavigation();
  const backNav = getBackNavigation(
    `/projects/${projectId}/queues`,
    'Back to Queues'
  );

  // State
  const [queue, setQueue] = useState<Queue | null>(null);
  const [circuitState, setCircuitState] = useState<CircuitBreakerState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // AC-435: Queue items state
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

  // AC-460: Data model and consumer mode state
  const [dataModel, setDataModel] = useState<Model | null>(null);
  const [singleConsumerModeUpdating, setSingleConsumerModeUpdating] = useState(false);
  const [activeConsumerCount, setActiveConsumerCount] = useState<number>(0);
  const [showSingleConsumerWarning, setShowSingleConsumerWarning] = useState(false);

  // Priority field edit state
  const [editingPriorityField, setEditingPriorityField] = useState(false);
  const [priorityFieldDraft, setPriorityFieldDraft] = useState('');
  const [priorityFieldUpdating, setPriorityFieldUpdating] = useState(false);

  // Numeric fields eligible as priority sources, detected from the data model
  const numericFields = useMemo(() => {
    if (!dataModel?.properties) return [];
    return Object.entries(dataModel.properties)
      .filter(([, type]) => type === 'int' || type === 'float')
      .map(([name]) => name);
  }, [dataModel]);

  // AC-464: Error queue relationship state
  const [errorQueue, setErrorQueue] = useState<Queue | null>(null);
  const [parentQueue, setParentQueue] = useState<Queue | null>(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);

  // History section expanded state
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Validate queueId is a number
  const numericQueueId = queueId ? parseInt(queueId, 10) : NaN;
  const isValidQueueId = !isNaN(numericQueueId);

  // AC-435: Fetch queue stats
  const fetchStats = useCallback(async () => {
    if (!isValidQueueId) return;
    try {
      const fetchedStats = await apiService.getQueueStats(numericQueueId);
      setStats(fetchedStats);
    } catch (err) {
      console.error('Failed to fetch queue stats:', err);
      // Stats are non-critical, continue with null
    }
  }, [numericQueueId, isValidQueueId]);

  // Fetch queue and circuit state
  useEffect(() => {
    const fetchQueueAndState = async () => {
      if (!isValidQueueId) {
        setError('Invalid queue ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch queue
        const fetchedQueue = await apiService.getQueue(numericQueueId);
        setQueue(fetchedQueue);

        // AC-460: Fetch data model for the queue
        if (fetchedQueue.data_model_id) {
          try {
            const model = await apiService.getModel(fetchedQueue.data_model_id);
            setDataModel(model);
          } catch (err) {
            console.error('Failed to fetch data model:', err);
            setDataModel(null);
          }
        }

        // AC-460: Fetch consumer count for single_consumer_mode warning
        try {
          const consumers = await apiService.getQueueConsumers(numericQueueId);
          const activeCount = consumers.filter(c => c.is_active && c.consumer_role !== 'BACKUP').length;
          setActiveConsumerCount(activeCount);
        } catch (err) {
          console.error('Failed to fetch consumer count:', err);
          setActiveConsumerCount(0);
        }

        // AC-464 / AC-1316: resolve the linked queue directly via the
        // single-queue GET. The list endpoint filters error queues out, so
        // scanning it for the error-queue child of a primary queue always
        // came up empty and rendered "Not created yet" even when the error
        // queue existed.
        try {
          if (fetchedQueue.is_error_queue && fetchedQueue.parent_queue_id) {
            const parent = await apiService.getQueue(fetchedQueue.parent_queue_id);
            setParentQueue(parent);
            setErrorQueue(null);
          } else if (fetchedQueue.error_queue_id) {
            const errQueue = await apiService.getQueue(fetchedQueue.error_queue_id);
            setErrorQueue(errQueue);
            setParentQueue(null);
          } else {
            setErrorQueue(null);
            setParentQueue(null);
          }
        } catch (err) {
          console.error('Failed to fetch queue relationships:', err);
          setErrorQueue(null);
          setParentQueue(null);
        }

        // Fetch circuit breaker state
        try {
          const state = await apiService.getCircuitBreakerState(numericQueueId);
          setCircuitState(state);
        } catch (err) {
          // If fetching circuit state fails, continue with null state (graceful degradation)
          console.error('Failed to fetch circuit state:', err);
          setCircuitState(null);
        }

        // AC-435: Fetch queue stats
        await fetchStats();
      } catch (err) {
        console.error('Failed to load queue:', err);
        setError('Failed to load queue details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchQueueAndState();
  }, [numericQueueId, isValidQueueId, fetchStats]);

  // Handle settings save callback
  const handleSettingsSave = async (settings: Partial<CircuitBreakerSettingsType>) => {
    if (!isValidQueueId) return;

    try {
      await apiService.updateQueue(numericQueueId, settings);
      // Refetch queue to get updated data
      const fetchedQueue = await apiService.getQueue(numericQueueId);
      setQueue(fetchedQueue);
    } catch (err) {
      console.error('Failed to save settings:', err);
      throw err; // Re-throw to let the settings component handle the error
    }
  };

  // Handle circuit breaker reset success
  const handleResetSuccess = async () => {
    if (!isValidQueueId) return;

    // Refetch circuit state after reset
    try {
      const state = await apiService.getCircuitBreakerState(numericQueueId);
      setCircuitState(state);
    } catch (err) {
      console.error('Failed to refetch circuit state:', err);
    }
  };

  // Get circuit breaker settings from queue
  const getCircuitBreakerSettings = (): CircuitBreakerSettingsType | undefined => {
    if (!queue) return undefined;
    return {
      circuit_breaker_enabled: queue.circuit_breaker_enabled,
      failure_rate_threshold: queue.failure_rate_threshold,
      consecutive_failures_threshold: queue.consecutive_failures_threshold,
      auto_reset_enabled: queue.auto_reset_enabled,
      auto_reset_cooldown_minutes: queue.auto_reset_cooldown_minutes,
    };
  };

  // Get scheduling settings from queue
  const getSchedulingSettings = () => {
    if (!queue) return undefined;
    return {
      auto_trigger_enabled: queue.auto_trigger_enabled ?? false,
      trigger_interval_minutes: queue.trigger_interval_minutes ?? null,
      trigger_item_threshold: queue.trigger_item_threshold ?? null,
      trigger_age_threshold_minutes: queue.trigger_age_threshold_minutes ?? null,
      skip_if_empty: queue.skip_if_empty ?? true,
      trigger_condition_mode: queue.trigger_condition_mode ?? 'OR' as const,
      last_triggered_at: queue.last_triggered_at ? String(queue.last_triggered_at) : null,
      next_trigger_at: queue.next_trigger_at ? String(queue.next_trigger_at) : null,
      drain_mode: queue.drain_mode ?? false,
      drain_max_cycles: queue.drain_max_cycles ?? 100,
      completed_retention_days: queue.completed_retention_days ?? null,
      max_total_failures: queue.max_total_failures ?? null,
    };
  };

  // AC-460: Single-consumer-mode toggle. Split so the warning dialog can
  // gate the API call: handle decides whether to warn, apply performs the
  // update. `newValue` is passed as an argument (not read from state) so
  // the user's intent at click time can't be invalidated by a refetch
  // arriving between warning open and confirm.
  const applySingleConsumerModeToggle = async (newValue: boolean) => {
    if (!isValidQueueId) return;
    setSingleConsumerModeUpdating(true);
    try {
      await apiService.updateQueue(numericQueueId, { single_consumer_mode: newValue });
      const fetchedQueue = await apiService.getQueue(numericQueueId);
      setQueue(fetchedQueue);
    } catch (err) {
      console.error('Failed to update single consumer mode:', err);
      toast.error('Failed to update consumer mode. Please try again.');
    } finally {
      setSingleConsumerModeUpdating(false);
      setShowSingleConsumerWarning(false);
    }
  };

  const handleSingleConsumerModeToggle = () => {
    if (!isValidQueueId || !queue) return;
    const newValue = !queue.single_consumer_mode;
    if (newValue && activeConsumerCount > 1) {
      setShowSingleConsumerWarning(true);
      return;
    }
    void applySingleConsumerModeToggle(newValue);
  };

  // AC-1418: Toggle the queue's user-controlled enable/disable flag without
  // touching the trigger configuration. Tracked separately from
  // singleConsumerMode so the loading state on the toggle button is
  // independent of the consumer-mode action.
  const [isEnabledUpdating, setIsEnabledUpdating] = useState(false);
  const handleToggleEnabled = async () => {
    if (!isValidQueueId || !queue) return;
    const next = !(queue.is_enabled ?? true);
    setIsEnabledUpdating(true);
    try {
      await apiService.updateQueue(numericQueueId, { is_enabled: next });
      const fetchedQueue = await apiService.getQueue(numericQueueId);
      setQueue(fetchedQueue);
      toast.success(next ? 'Queue enabled' : 'Queue paused');
    } catch (err) {
      console.error('Failed to toggle queue enabled state:', err);
      toast.error('Failed to update queue. Please try again.');
    } finally {
      setIsEnabledUpdating(false);
    }
  };

  // Handle scheduling settings save callback
  const handleSchedulingSettingsSave = async (_settings: SchedulingSettings) => {
    if (!isValidQueueId) return;

    try {
      // Refetch queue to get updated data (including next_trigger_at)
      const fetchedQueue = await apiService.getQueue(numericQueueId);
      setQueue(fetchedQueue);
    } catch (err) {
      console.error('Failed to refetch queue after scheduling settings save:', err);
    }
  };

  // Handle queue name/description/display-columns edit save (AC-1482: consolidated)
  const handleEditSave = async (data: {
    name: string;
    description: string;
    display_payload_fields: string[];
  }) => {
    if (!isValidQueueId) return;

    await apiService.updateQueue(numericQueueId, {
      name: data.name,
      description: data.description,
      display_payload_fields: data.display_payload_fields,
    });
    // Refetch queue to get updated data
    const fetchedQueue = await apiService.getQueue(numericQueueId);
    setQueue(fetchedQueue);
  };

  // Save an edit to priority_field.
  // Empty string clears the priority field (queue becomes FIFO).
  const handlePriorityFieldSave = async () => {
    if (!isValidQueueId) return;
    setPriorityFieldUpdating(true);
    try {
      const trimmed = priorityFieldDraft.trim();
      await apiService.updateQueue(numericQueueId, {
        // Send empty string when the user cleared it so the backend sets it to null
        priority_field: trimmed || '',
      });
      const fetchedQueue = await apiService.getQueue(numericQueueId);
      setQueue(fetchedQueue);
      setEditingPriorityField(false);
      toast.success(trimmed ? `Priority field set to "${trimmed}"` : 'Priority field cleared');
    } catch (err) {
      console.error('Failed to update priority field:', err);
      toast.error('Failed to update priority field');
    } finally {
      setPriorityFieldUpdating(false);
    }
  };

  // Refresh queue data - called after undo/reapply from version history
  const refreshQueue = async () => {
    if (!isValidQueueId) return;
    try {
      const fetchedQueue = await apiService.getQueue(numericQueueId);
      setQueue(fetchedQueue);
    } catch (err) {
      console.error('Failed to refresh queue after history change:', err);
    }
  };

  // AC-435: Queue Items Handlers
  const handleViewItem = (item: QueueItem) => {
    setSelectedItem(item);
    setIsItemModalOpen(true);
  };

  const handleCloseItemModal = () => {
    setIsItemModalOpen(false);
    setSelectedItem(null);
  };

  const handleRetryItem = async (itemId: string) => {
    if (!isValidQueueId) return;
    await apiService.retryQueueItem(numericQueueId, itemId);
    await fetchStats();
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!isValidQueueId) return;
    await apiService.deleteQueueItem(numericQueueId, itemId);
    await fetchStats();
  };

  const handlePurgeQueue = async () => {
    if (!isValidQueueId) return;
    setActionLoading(true);
    try {
      const result = await apiService.purgeQueue(numericQueueId);
      toast.success(`Purged ${result.deleted_count} items from the queue.`);
      setShowPurgeConfirm(false);
      await fetchStats();
    } catch (err) {
      console.error('Failed to purge queue:', err);
      toast.error('Failed to purge queue. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryAllFailed = async () => {
    if (!isValidQueueId) return;
    if (!stats || stats.failed === 0) {
      toast('No failed items to retry.');
      return;
    }

    setActionLoading(true);
    try {
      const result = await apiService.retryAllFailedItems(numericQueueId);
      toast.success(`Queued ${result.retried_count} items for retry.`);
      await fetchStats();
    } catch (err) {
      console.error('Failed to retry all failed items:', err);
      toast.error('Failed to retry items. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Get circuit status display text
  const getCircuitStatusText = (): string => {
    if (!circuitState) return 'Unknown';
    if (!circuitState.enabled) return 'Disabled';
    if (circuitState.state === 'OPEN') return 'Paused';
    return 'Active';
  };

  // Get circuit status color class
  const getCircuitStatusClass = (): string => {
    if (!circuitState || !circuitState.enabled) return 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    if (circuitState.state === 'OPEN') return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300';
    return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300';
  };

  // Invalid queueId error
  if (!isValidQueueId) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-red-700 dark:text-red-300">Invalid queue ID</div>
          </div>
        </div>
        <Link
          to={backNav.path}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {backNav.label}
        </Link>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading queue details...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-red-700 dark:text-red-300">{error}</div>
          </div>
        </div>
        <Link
          to={backNav.path}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {backNav.label}
        </Link>
      </div>
    );
  }

  // No queue found
  if (!queue) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-yellow-700 dark:text-yellow-300">Queue not found</div>
          </div>
        </div>
        <Link
          to={backNav.path}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {backNav.label}
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Back Link */}
      <div className="mb-6">
        <Link
          to={backNav.path}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {backNav.label}
        </Link>
      </div>

      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">{queue.name}</h1>
          <Tooltip content="Edit queue name, description, and display columns">
          <button
            onClick={() => setShowEditModal(true)}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Edit queue"
            data-testid="edit-queue-button"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          </Tooltip>
          {/* AC-1418: pause/resume toggle. Status badge shows the current
              state; the button toggles it. Trigger config is preserved. */}
          {!queue.is_error_queue && (() => {
            const enabled = queue.is_enabled ?? true;
            return (
              <>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    enabled
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                  data-testid="queue-enabled-status"
                >
                  {enabled ? 'Active' : 'Inactive'}
                </span>
                <Tooltip content={enabled ? 'Pause queue (preserves trigger configuration)' : 'Resume queue'}>
                  <button
                    onClick={handleToggleEnabled}
                    disabled={isEnabledUpdating}
                    className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                    aria-label={enabled ? 'Pause queue' : 'Resume queue'}
                    data-testid="queue-enabled-toggle"
                  >
                    {isEnabledUpdating
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                </Tooltip>
              </>
            );
          })()}
          {queue.is_error_queue && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              Error Queue
            </span>
          )}
        </div>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{queue.description || 'No description'}</p>
      </div>

      {/* AC-464: Error Queue Notice Banner */}
      {queue.is_error_queue && parentQueue && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                This is an error queue for failed items
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                Items that failed processing in{' '}
                <Link
                  to={`/projects/${projectId}/queues/${parentQueue.queue_id}`}
                  className="font-medium text-amber-800 hover:text-amber-900 underline inline-flex items-center"
                >
                  {parentQueue.name}
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Link>{' '}
                are moved here after exceeding the retry limit.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Queue Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Queue Info Card */}
        <div className="bg-white dark:bg-cap-dark shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-warm-white mb-4">Queue Information</h2>
          <dl className="space-y-3">
            {/* Current State reports circuit-breaker state; AC-1324: hide on
                error queues since they don't have a breaker. */}
            {!queue.is_error_queue && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Current State</dt>
                <dd className="mt-1">
                  <span
                    data-testid="circuit-status-badge"
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCircuitStatusClass()}`}
                  >
                    {getCircuitStatusText()}
                  </span>
                </dd>
              </div>
            )}
            {/* AC-460: Data Model Display */}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Model</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-warm-white">
                {dataModel ? dataModel.class_name : 'Loading...'}
              </dd>
            </div>
            {/* AC-460: Max Retry Count */}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Max Retry Count</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-warm-white">
                {queue.max_retry_count}
              </dd>
            </div>
            {/* AC-464: Error Queue Relationship */}
            {!queue.is_error_queue && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Error Queue</dt>
                <dd className="mt-1 text-sm">
                  {errorQueue ? (
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/projects/${projectId}/queues/${errorQueue.queue_id}`}
                        className="text-primary-600 hover:text-primary-800 inline-flex items-center"
                      >
                        {errorQueue.name}
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Link>
                      {queue.error_queue_item_count != null && (
                        <span
                          className={
                            queue.error_queue_item_count > 0
                              ? 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              : 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }
                        >
                          {queue.error_queue_item_count} {queue.error_queue_item_count === 1 ? 'error' : 'errors'}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500">Not created yet</span>
                  )}
                </dd>
              </div>
            )}
            {queue.is_error_queue && parentQueue && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Parent Queue</dt>
                <dd className="mt-1 text-sm">
                  <Link
                    to={`/projects/${projectId}/queues/${parentQueue.queue_id}`}
                    className="text-primary-600 hover:text-primary-800 inline-flex items-center"
                  >
                    {parentQueue.name}
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Link>
                </dd>
              </div>
            )}
            {/* AC-460: Single Consumer Mode Toggle
                AC-1324: Hidden on error queues — error queues don't have
                consumers, they only accept items moved in from the parent. */}
            {!queue.is_error_queue && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Consumer Mode</dt>
                <dd className="mt-1">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSingleConsumerModeToggle}
                      disabled={singleConsumerModeUpdating}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        queue.single_consumer_mode
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      } ${singleConsumerModeUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="single-consumer-mode-toggle"
                    >
                      {singleConsumerModeUpdating ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : null}
                      {queue.single_consumer_mode ? 'Single Consumer' : 'Multiple Consumers'}
                    </button>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({activeConsumerCount} active consumer{activeConsumerCount !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {/* Warning if single mode enabled but multiple consumers exist */}
                  {queue.single_consumer_mode && activeConsumerCount > 1 && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {activeConsumerCount} consumers still active - setting only blocks new registrations
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {queue.single_consumer_mode
                      ? 'Only one workflow can read from this queue'
                      : 'Multiple workflows can read concurrently'}
                  </p>
                </dd>
              </div>
            )}
            {/* Priority queue indicator + editor */}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Read Order</dt>
              <dd className="mt-1">
                {editingPriorityField ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    {numericFields.length > 0 ? (
                      <select
                        value={priorityFieldDraft}
                        onChange={(e) => setPriorityFieldDraft(e.target.value)}
                        disabled={priorityFieldUpdating}
                        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">FIFO (no priority)</option>
                        {numericFields.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={priorityFieldDraft}
                        onChange={(e) => setPriorityFieldDraft(e.target.value)}
                        disabled={priorityFieldUpdating}
                        placeholder="Field name (empty = FIFO)"
                        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
                      />
                    )}
                    <button
                      onClick={handlePriorityFieldSave}
                      disabled={priorityFieldUpdating}
                      className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {priorityFieldUpdating ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingPriorityField(false);
                        setPriorityFieldDraft(queue.priority_field || '');
                      }}
                      disabled={priorityFieldUpdating}
                      className="px-3 py-1 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    {queue.priority_field ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                        Priority by <code className="ml-1 font-mono">{queue.priority_field}</code> (highest first)
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                        FIFO (oldest first)
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setPriorityFieldDraft(queue.priority_field || '');
                        setEditingPriorityField(true);
                      }}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 underline"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-warm-white">
                {queue.created_at
                  ? new Date(queue.created_at).toLocaleString()
                  : 'N/A'}
              </dd>
            </div>
            {queue.updated_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Updated</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-warm-white">
                  {new Date(queue.updated_at).toLocaleString()}
                </dd>
              </div>
            )}
            {!queue.is_error_queue && circuitState?.state === 'OPEN' && circuitState.reason && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Trip Reason</dt>
                <dd className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">{circuitState.reason}</dd>
              </div>
            )}
            {!queue.is_error_queue && circuitState?.state === 'OPEN' && circuitState.opened_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Tripped At</dt>
                <dd className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                  {new Date(circuitState.opened_at).toLocaleString()}
                </dd>
              </div>
            )}
          </dl>

          {/* Reset Button — AC-1324: hidden on error queues since they have no breaker */}
          {!queue.is_error_queue && (
            <div className="mt-6">
              <ResetCircuitBreakerButton
                queueId={numericQueueId}
                circuitState={circuitState || undefined}
                onSuccess={handleResetSuccess}
              />
            </div>
          )}
        </div>

        {/* Circuit Breaker Settings Card — AC-1324: hidden on error queues */}
        {!queue.is_error_queue && (
          <div className="bg-white dark:bg-cap-dark shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white">Breaker Configuration</h2>
            </div>
            <CircuitBreakerSettings
              queueId={numericQueueId}
              settings={getCircuitBreakerSettings()}
              onSave={handleSettingsSave}
            />
          </div>
        )}
      </div>

      {/* Queue Scheduling Settings Card — AC-1324: hidden on error queues
          since nothing auto-triggers off an error queue. Items only enter
          via the parent's move-to-error path and leave via retry/delete. */}
      {!queue.is_error_queue && (
        <div className="mb-8">
          <div className="bg-white dark:bg-cap-dark shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white">Scheduling Configuration</h2>
            </div>
            <QueueSchedulingSettings
              queueId={numericQueueId}
              initialSettings={getSchedulingSettings()}
              onSave={handleSchedulingSettingsSave}
            />
          </div>
        </div>
      )}

      {/* AC-462: Consumer Registry Panel — AC-1324: hidden on error queues */}
      {!queue.is_error_queue && (
        <div className="mb-8">
          <div className="bg-white dark:bg-cap-dark shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white">Consumer Registry</h2>
            </div>
            <ConsumerRegistryPanel
              queueId={numericQueueId}
              projectId={projectId || ''}
              singleConsumerMode={queue.single_consumer_mode}
            />
          </div>
        </div>
      )}

      {/* Version History Section */}
      <div className="mb-8">
        <div className="bg-white dark:bg-cap-dark shadow rounded-lg">
          <button
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            data-testid="history-section-toggle"
          >
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white">Version History</h2>
            </div>
            <span className="text-gray-400 dark:text-gray-500 text-sm">
              {historyExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
          </button>
          {historyExpanded && (
            <div className="p-4">
              <EntityHistoryTab
                entityType="queue"
                entityId={numericQueueId}
                projectId={parseInt(projectId || '0', 10)}
                onEntityChanged={refreshQueue}
              />
            </div>
          )}
        </div>
      </div>

      {/* AC-460: Data Model Schema Card */}
      {dataModel && (
        <div className="mb-8">
          <div className="bg-white dark:bg-cap-dark shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white">Data Model Schema</h2>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Model: {dataModel.class_name}</h3>
                {dataModel.description && (
                  <p className="mt-1 text-sm text-gray-500">{dataModel.description}</p>
                )}
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Properties</h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <ul className="space-y-2">
                    {Object.entries(dataModel.properties).map(([key, value]) => (
                      <li key={key} className="flex items-center text-sm">
                        <span className="font-mono text-gray-800 dark:text-gray-200">{key}</span>
                        <span className="mx-2 text-gray-400">:</span>
                        <span className="text-gray-600 dark:text-gray-400">{String(value)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AC-435: Queue Stats Summary */}
      {stats && (
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-warm-white">Queue Statistics</h2>
            <div className="flex items-center space-x-2">
              {stats.failed > 0 && (
                <button
                  onClick={handleRetryAllFailed}
                  disabled={actionLoading}
                  className="inline-flex items-center px-3 py-1.5 text-sm text-primary-600 dark:text-blue-400 border border-primary-300 dark:border-blue-700 rounded-md hover:bg-primary-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Retry All Failed
                </button>
              )}
              <button
                onClick={() => setShowPurgeConfirm(true)}
                disabled={actionLoading || stats.total === 0}
                className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Purge Queue
              </button>
            </div>
          </div>
          {/* AC-1325: on error queues every item is FAILED, so the other
              four cards are always 0. Show just the Failed card. */}
          <div className={queue.is_error_queue
            ? 'grid grid-cols-1 gap-4'
            : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4'}>
            {!queue.is_error_queue && (
              <>
                {/* Total */}
                <div className="bg-white dark:bg-cap-dark shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total</p>
                      <p className="text-xl font-semibold text-gray-900 dark:text-warm-white">{stats.total}</p>
                    </div>
                  </div>
                </div>
                {/* Pending */}
                <div className="bg-white dark:bg-cap-dark shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending</p>
                      <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">{stats.pending}</p>
                    </div>
                  </div>
                </div>
                {/* Processing */}
                <div className="bg-white dark:bg-cap-dark shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <Play className="w-5 h-5 text-primary-500 dark:text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Processing</p>
                      <p className="text-xl font-semibold text-primary-600">{stats.processing}</p>
                    </div>
                  </div>
                </div>
                {/* Completed */}
                <div className="bg-white dark:bg-cap-dark shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</p>
                      <p className="text-xl font-semibold text-green-600">{stats.completed}</p>
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* Failed (always shown; the only meaningful count on error queues) */}
            <div className="bg-white dark:bg-cap-dark shadow rounded-lg p-4">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Failed</p>
                  <p className="text-xl font-semibold text-red-600">{stats.failed}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AC-1475/1480: Priority histogram visualization */}
      <div className="mb-4">
        <PriorityHistogram queue={queue} />
      </div>

      {/* AC-1475: Display-columns toolbar — discoverable entry point to the
          payload field projection settings (which live inside QueueEditModal). */}
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2 flex-wrap">
          <span className="font-medium">Display columns:</span>
          {(queue.display_payload_fields ?? []).length === 0 ? (
            <span className="italic">payload preview (default)</span>
          ) : (
            (queue.display_payload_fields ?? []).map((path) => (
              <span
                key={path}
                className="inline-block px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 font-mono"
              >
                {path}
              </span>
            ))
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowEditModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-500"
          data-testid="configure-columns-button"
        >
          <Columns className="w-3.5 h-3.5" />
          Configure
        </button>
      </div>

      {/* AC-435: Queue Items List */}
      <div className="mb-8">
        <QueueItemsList
          queueId={numericQueueId}
          priorityField={queue.priority_field}
          isErrorQueue={queue.is_error_queue}
          displayPayloadFields={queue.display_payload_fields ?? []}
          onViewItem={handleViewItem}
          onRefresh={fetchStats}
        />
      </div>

      {/* AC-435: Queue Item Details Modal */}
      {selectedItem && (
        <QueueItemDetailsModal
          item={selectedItem}
          isOpen={isItemModalOpen}
          onClose={handleCloseItemModal}
          onRetry={handleRetryItem}
          onDelete={handleDeleteItem}
        />
      )}

      {/* Queue Edit Modal */}
      <QueueEditModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleEditSave}
        queue={queue}
      />

      {showSingleConsumerWarning && (
        <ConfirmationDialog
          title="Enable single-consumer mode?"
          message={
            `This queue currently has ${activeConsumerCount} active consumers. ` +
            `Enabling single-consumer mode will NOT remove existing consumers - they will continue to operate. ` +
            `This setting only prevents NEW consumers from registering. ` +
            `To enforce single-consumer access, first remove extra consumers from the queue. Continue anyway?`
          }
          confirmText="Enable anyway"
          confirmVariant="warning"
          isLoading={singleConsumerModeUpdating}
          onConfirm={() => void applySingleConsumerModeToggle(true)}
          onCancel={() => setShowSingleConsumerWarning(false)}
        />
      )}

      {/* Purge Queue Confirmation Dialog */}
      {showPurgeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
                Purge All Queue Items
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This will permanently delete all {stats?.total ?? 0} {stats?.total === 1 ? 'item' : 'items'} from this queue. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPurgeConfirm(false)}
                disabled={actionLoading}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handlePurgeQueue}
                disabled={actionLoading}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Purging...' : 'Purge All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueDetailsPage;
